import React, { useEffect, useMemo, useState, useContext } from "react";
import { Spin, Alert, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../../contexts/AuthContext";
import { TeacherAPI } from "../../../../api/teacherAPI";
import { TeacherTasksGrid } from "../../../../components/Admin/TeacherTasksComponent/TeacherTasksGrid";
import { PageHeader, BrandThemeProvider } from "../../../../ui";
import { useThemeRuntime } from "../../../../ui/theme/ThemeRuntimeProvider";
import { NEW_TAREAS } from "../../../../ui/featureFlags";

import paso1 from "../../../../assets/illustrations/guia-tareas/paso1.png";
import paso2 from "../../../../assets/illustrations/guia-tareas/paso2.png";
import paso3 from "../../../../assets/illustrations/guia-tareas/paso3.png";
import paso4 from "../../../../assets/illustrations/guia-tareas/paso4.png";
import paso5 from "../../../../assets/illustrations/guia-tareas/paso5.png";

import styles from "./Page_Teacher_Tareas.module.scss";

export function Page_Teacher_Tareas() {
  const { t } = useTranslation("teachers");
  const { t: tt } = useTranslation("tasks");
  const { user, accessToken } = useContext(AuthContext);
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const newTaskButton = NEW_TAREAS ? (
    <BrandThemeProvider>
      <Button
        type="primary"
        size="large"
        icon={<PlusOutlined />}
        onClick={() => setCreateOpen(true)}
      >
        {tt("list.newTask")}
      </Button>
    </BrandThemeProvider>
  ) : null;

  const teacherApi = useMemo(() => new TeacherAPI(), []);

  const teacherId = useMemo(() => {
    return user?.user?.teacher || user?.teacher || null;
  }, [user]);

  // Overrides del editor de estilos (guia rapida por pagina). En la sesion de un
  // diseñador ganan; para el resto, valor por defecto (i18n + asset).
  const { resolvedOverrides } = useThemeRuntime();
  const ov = (key, fallback) => {
    const v = resolvedOverrides[key];
    return typeof v === "string" && v.trim() ? v : fallback;
  };
  const guideTitleOv = ov("text.tareas.guide.title", undefined);

  const headerSteps = useMemo(
    () => {
      const tareaImgs = [paso1, paso2, paso3, paso4, paso5];
      return [1, 2, 3, 4, 5].map((n) => ({
        title: ov(
          `text.tareas.guide.step${n}.title`,
          t(`pages.tareasStep${n}`),
        ),
        description: ov(
          `text.tareas.guide.step${n}.desc`,
          t(`pages.tareasStep${n}Desc`),
        ),
        illustration: ov(`image.tareas.guide.step${n}`, tareaImgs[n - 1]),
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, resolvedOverrides],
  );

  useEffect(() => {
    document.title = t("pages.tareasTitle") + " | Educa360";
  }, [t]);

  useEffect(() => {
    let isMounted = true;

    async function fetchTeacher() {
      try {
        setLoading(true);
        setLoadError(null);

        if (!accessToken || !teacherId) {
          throw new Error(t("pages.sessionError"));
        }

        const resp = await teacherApi.getTeacherById(accessToken, teacherId);
        if (resp?.code !== 200 || !resp?.teacher) {
          throw new Error(resp?.message || t("pages.teacherLoadError"));
        }

        if (isMounted) setTeacher(resp.teacher);
      } catch (err) {
        if (isMounted) {
          setLoadError(
            err?.message || "Error inesperado al cargar el docente.",
          );
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchTeacher();
    return () => {
      isMounted = false;
    };
  }, [accessToken, teacherId, teacherApi, t]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
        <p style={{ marginTop: 12, color: "#6b7280" }}>
          {t("pages.loadingTeacher")}
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.page}>
        <PageHeader
          title={t("pages.tareasTitle")}
          description={t("pages.tareasDesc")}
          steps={headerSteps}
        />
        <Alert
          type="error"
          title={t("pages.pageLoadError")}
          description={loadError}
          showIcon
        />
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className={styles.page}>
        <PageHeader
          title={t("pages.tareasTitle")}
          description={t("pages.tareasDesc")}
          steps={headerSteps}
        />
        <Alert
          type="warning"
          title={t("pages.teacherNotFound")}
          description={t("pages.teacherNotFoundDesc")}
          showIcon
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={t("pages.tareasTitle")}
        description={t("pages.tareasDesc")}
        steps={headerSteps}
        guideTitle={guideTitleOv}
        extra={newTaskButton}
      />

      <TeacherTasksGrid
        teacher={teacher}
        accessToken={accessToken}
        createOpen={createOpen}
        onCloseCreate={() => setCreateOpen(false)}
      />
    </div>
  );
}
